import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import QRCode from 'qrcode';
import { api, money } from '../api.js';
import { errMessage } from '../i18n.js';
import { useT } from '../lang.js';
import Icon from '../Icon.jsx';
import { MENU_TEMPLATES, templateByKey } from '../menuTemplates.js';
import { catIconUrl } from '../catIcons.js';
import PageSkeleton, { SkRows } from '../Skeleton.jsx';

/**
 * Yazdırma merkezi: QR kartları ve menü şablonları.
 *
 * NEDEN BÖYLE:
 *  · PDF SUNUCUDA üretilmiyor — tarayıcının yazdırma penceresi zaten "PDF
 *    olarak kaydet" sunuyor; ne headless tarayıcı ne ayrı PDF kütüphanesi.
 *  · Üç biçim üç ayrı işi karşılar: toplu sayfa (bir kerede kes-dağıt),
 *    teker teker (masa kartı/standı), menü (QR'ı menünün köşesine basıp tek
 *    kâğıtla idare etmek).
 *  · Menü ŞABLONU elle doldurulmaz: paneldeki güncel menüden dolar. Fiyat
 *    değişince yeniden yazdırmak yeter, ikinci bir "basılı menü" verisi
 *    tutulmaz (tutulsaydı er geç menüyle ayrışırdı).
 *  · Basılı menüde `available=false` ürünler DE kalır: o bayrak günlük stok
 *    ("bugün yok") demek, kâğıdı her akşam yeniden bastırmak anlamsız.
 *    Gizli kategori ise misafire hiç açılmaz, basılmaz.
 *  · Tasarım şablonlarında arka plan `<img>` ile konur: tarayıcı yazdırırken
 *    ARKA PLAN GRAFİKLERİNİ varsayılan olarak atar, resmi atmaz.
 */

const MODES = ['sheet', 'single', 'menu'];
const PLAIN = ['classic', 'compact', 'elegant'];

export default function QrSheet() {
  const t = useT();
  const [sp] = useSearchParams();
  const [data, setData] = useState(null);
  const [menu, setMenu] = useState(null);
  const [err, setErr] = useState('');
  const [size, setSize] = useState(220);
  // Menü ekranından "Menüyü yazdır" ile gelindiğinde doğrudan o biçim açılır.
  const [mode, setMode] = useState(MODES.includes(sp.get('mode')) ? sp.get('mode') : 'sheet');
  const [tpl, setTpl] = useState('ottoman');
  const [only, setOnly] = useState('');
  // Hangi kategoriler basılacak: varsayılan HEPSİ. Bölmeli şablonlarda
  // kapasite sınırlı olduğu için seçim şart oluyor.
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    api('/api/tables/qr-sheet').then(setData).catch((e) => setErr(errMessage(t, e)));
    // eslint-disable-next-line
  }, []);

  // Menü yalnız gerektiğinde indirilir: QR basmaya gelenlerin çoğu menü
  // şablonunu hiç açmıyor.
  useEffect(() => {
    if (mode !== 'menu' || menu) return;
    api('/api/menu').then((m) => {
      setMenu(m);
      setPicked(new Set(m.categories.filter((c) => c.visible && c.items.length).map((c) => c.id)));
    }).catch((e) => setErr(errMessage(t, e)));
    // eslint-disable-next-line
  }, [mode]);

  if (err && !data) return <p className="err">{err}</p>;
  if (!data) return <PageSkeleton shape="form" />;

  const design = templateByKey(tpl);
  const tables = only ? data.tables.filter((x) => String(x.id) === only) : data.tables;
  const allCats = (menu?.categories || []).filter((c) => c.visible && c.items.length > 0);
  const chosen = allCats.filter((c) => !picked || picked.has(c.id));
  const max = design?.slots ? design.slots.length : null;
  const cats = max ? chosen.slice(0, max) : chosen;
  const trimmed = max ? chosen.length - cats.length : 0;

  return (
    <>
      {/* Tasarım şablonu kâğıdın kenarına kadar basar: sayfa payı sıfırlanır.
          Sade şablonlar ve QR kartları payı korur (styles.css @page). */}
      {mode === 'menu' && design && <style>{'@page { margin: 0 }'}</style>}

      <div className="card no-print">
        <div className="row">
          <h2 style={{ margin: 0 }}>{t('qr.title', { n: data.tables.length })}</h2>
          <span className="spacer" />
          <button className="primary" onClick={() => window.print()}><Icon n="printer" />{t('qr.print')}</button>
        </div>

        <div className="row" style={{ marginTop: 12 }}>
          <div style={{ minWidth: 190 }}>
            <label>{t('qr.mode')}</label>
            <select value={mode} onChange={(e) => setMode(e.target.value)}>
              {MODES.map((m) => <option key={m} value={m}>{t(`qr.mode.${m}`)}</option>)}
            </select>
          </div>

          {mode === 'menu' && (
            <div style={{ minWidth: 210 }}>
              <label>{t('qr.template')}</label>
              <select value={tpl} onChange={(e) => setTpl(e.target.value)}>
                <optgroup label={t('qr.tplDesign')}>
                  {MENU_TEMPLATES.map((x) => <option key={x.key} value={x.key}>{t(`qr.tpl.${x.key}`)}</option>)}
                </optgroup>
                <optgroup label={t('qr.tplPlain')}>
                  {PLAIN.map((x) => <option key={x} value={x}>{t(`qr.tpl.${x}`)}</option>)}
                </optgroup>
              </select>
            </div>
          )}

          <div style={{ minWidth: 170 }}>
            <label>{t('qr.table')}</label>
            <select value={only} onChange={(e) => setOnly(e.target.value)}>
              <option value="">{t('qr.allTables')}</option>
              {data.tables.map((tb) => (
                <option key={tb.id} value={tb.id}>{tb.areaName ? `${tb.areaName} · ${tb.name}` : tb.name}</option>
              ))}
            </select>
          </div>

          {mode !== 'menu' && (
            <div style={{ minWidth: 150 }}>
              <label>{t('qr.size')}</label>
              <input type="range" min="140" max="320" value={size}
                onChange={(e) => setSize(Number(e.target.value))} />
            </div>
          )}
        </div>

        <p className="muted" style={{ marginBottom: 0, marginTop: 12 }}>
          {mode === 'menu' ? t('qr.menuNote') : t('qr.note')}
        </p>
      </div>

      {mode === 'menu' && allCats.length > 0 && (
        <div className="card no-print">
          <div className="row">
            <b>{t('qr.categories')}</b>
            {max && <span className="muted">{t('qr.maxCats', { n: max })}</span>}
            <span className="spacer" />
            {trimmed > 0 && <span className="badge warn">{t('qr.catsTrimmed', { n: trimmed })}</span>}
          </div>
          <div className="row" style={{ marginTop: 10 }}>
            {allCats.map((c) => (
              <label className="switch" key={c.id}>
                <input type="checkbox" checked={!picked || picked.has(c.id)}
                  onChange={() => setPicked((prev) => {
                    const next = new Set(prev || allCats.map((x) => x.id));
                    next.has(c.id) ? next.delete(c.id) : next.add(c.id);
                    return next;
                  })} />
                <span>{c.name}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {mode === 'sheet' && (
        <div className="qr-grid">
          {tables.map((tb) => (
            <div className="qr-card" key={tb.id}>
              <Qr text={tb.url} size={size} label={t('qr.failed')} />
              <div style={{ fontWeight: 700, fontSize: 18, marginTop: 8 }}>{tb.name}</div>
              {tb.areaName && <div className="muted" style={{ fontSize: 13 }}>{tb.areaName}</div>}
              <div style={{ fontSize: 12, marginTop: 6, opacity: .75 }}>{data.restaurant.name}</div>
              <div style={{ fontSize: 10, marginTop: 2, opacity: .5 }}>v{tb.qrVersion}</div>
            </div>
          ))}
        </div>
      )}

      {mode === 'single' && (
        <div className="qr-singles">
          {tables.map((tb) => (
            <section className="qr-page" key={tb.id}>
              <div className="qr-big">
                <div className="qr-rest">{data.restaurant.name}</div>
                <Qr text={tb.url} size={Math.round(size * 1.6)} label={t('qr.failed')} />
                <div className="qr-tname">{tb.name}</div>
                {tb.areaName && <div className="muted">{tb.areaName}</div>}
                <p className="qr-cta">{t('qr.scanToOrder')}</p>
                <div className="qr-ver">v{tb.qrVersion}</div>
              </div>
            </section>
          ))}
        </div>
      )}

      {mode === 'menu' && (!menu ? <div className="card"><SkRows n={4} /></div> : cats.length === 0 ? (
        <div className="card"><h2>{t('menu.empty')}</h2>
          <p className="muted" style={{ margin: 0 }}>{t('menu.emptyNote', { currency: menu.currency })}</p></div>
      ) : (
        <div className="mn-set">
          {tables.map((tb) => (design
            ? <DesignPage key={tb.id} tpl={design} rest={data.restaurant} table={tb}
                cats={cats} currency={menu.currency} t={t} />
            : <PlainPage key={tb.id} tpl={tpl} rest={data.restaurant} table={tb}
                cats={cats} currency={menu.currency} t={t} />
          ))}
        </div>
      ))}

      {err && <p className="err no-print">{err}</p>}
    </>
  );
}

/* ── Sade şablonlar: düz kâğıda, mürekkep harcamadan ───────────────────── */
function PlainPage({ tpl, rest, table, cats, currency, t }) {
  return (
    <article className={`mn-page mn-${tpl}`}>
      <header className="mn-head">
        <div>
          <h1>{rest.name}</h1>
          <p className="mn-table">{table.areaName ? `${table.areaName} · ${table.name}` : table.name}</p>
        </div>
        <div className="mn-qr">
          <Qr text={table.url} size={118} label={t('qr.failed')} />
          <span>{t('qr.scanToOrder')}</span>
        </div>
      </header>
      <div className="mn-body">
        {cats.map((c) => (
          <section className="mn-cat" key={c.id}>
            <h2>{c.icon && <img className="mn-ico" src={catIconUrl(c.icon)} alt="" />}{c.name}</h2>
            {c.items.map((it) => (
              <div className="mn-item" key={it.id}>
                <div className="mn-line">
                  <span className="mn-nm">{it.name}</span>
                  <span className="mn-dots" aria-hidden="true" />
                  <span className="mn-pr">{money(it.priceMinor, currency)}</span>
                </div>
                {tpl !== 'compact' && it.description && <p className="mn-ds">{it.description}</p>}
              </div>
            ))}
          </section>
        ))}
      </div>
    </article>
  );
}

/* ── Tasarım şablonları ────────────────────────────────────────────────── */
const box = (r) => ({
  left: `${r.x}%`, top: `${r.y}%`, width: `${r.w}%`,
  ...(r.h ? { height: `${r.h}%` } : null),
  // Tek sütunda `column-count` VERİLMEZ: sütun kutusu taşan içeriği aşağı
  // değil YANA taşırır, o yüzden `scrollHeight` taşmayı hiç göstermez
  // (sığdırma döngüsü yazıyı büyütüp kategorileri kırpıyordu).
  ...(r.cols > 1 ? { columnCount: r.cols, columnGap: `${r.gap || 5}%` } : null),
});
const dot = (d) => ({
  left: `${d.cx - d.size / 2}%`, top: `${d.cy}%`, width: `${d.size}%`,
  aspectRatio: '1', transform: 'translateY(-50%)',
});

function DesignPage({ tpl, rest, table, cats, currency, t }) {
  const pageRef = useRef(null);
  useFitPage(pageRef, [tpl.key, cats, table.id]);

  const rows = (c) => c.items.map((it) => (
    <div className="dz-row" key={it.id}>
      <span className="dz-nm">{it.name}</span>
      <span className="dz-dots" aria-hidden="true" />
      <span className="dz-pr">{money(it.priceMinor, currency)}</span>
    </div>
  ));

  return (
    <article className="dz" ref={pageRef} style={{ '--ink': tpl.ink, '--soft': tpl.soft, '--rule': tpl.rule }}>
      <img className="dz-bg" src={tpl.bg} alt="" />

      <div className="dz-el" style={box(tpl.title)}>
        <div className={tpl.title.onDark ? 'dz-name on-dark' : 'dz-name'}
          style={{ textAlign: tpl.title.align }}>{rest.name}</div>
      </div>

      <div className="dz-el dz-qr" style={dot(tpl.qr)}>
        <Qr text={table.url} size={420} label={t('qr.failed')} fill />
      </div>
      <div className={tpl.cap.onDark ? 'dz-el dz-cap on-dark' : 'dz-el dz-cap'} style={box(tpl.cap)}>
        <b>{table.name}</b><br />{t('qr.scanToOrder')}
      </div>

      {tpl.body ? (
        <div className="dz-el dz-body dz-fit" style={box(tpl.body)}>
          {cats.map((c) => (
            <section className="dz-cat" key={c.id}>
              <h2><Disc tpl={tpl} icon={c.icon} />{c.name}</h2>
              {rows(c)}
            </section>
          ))}
        </div>
      ) : tpl.slots.map((slot, i) => cats[i] && (
        <React.Fragment key={cats[i].id}>
          {cats[i].icon && (
            <img className="dz-el dz-ico" src={catIconUrl(cats[i].icon)} alt="" style={dot(slot.icon)} />
          )}
          <div className="dz-el dz-pill" style={box(slot.name)}>
            <span>{cats[i].name}</span>
          </div>
          <div className="dz-el dz-items dz-fit" style={box(slot.items)}>{rows(cats[i])}</div>
        </React.Fragment>
      ))}
    </article>
  );
}

/** Kategori dairesi: tasarımdan silinen daireyi biz çiziyoruz (SVG, çünkü
 *  arka plan rengi yazdırılmaz) — simge varsa içine oturur. */
function Disc({ tpl, icon }) {
  if (!tpl.disc) return null;
  return (
    <span className="dz-disc">
      <svg viewBox="0 0 100 100" aria-hidden="true"><circle cx="50" cy="50" r="50" fill={tpl.disc} /></svg>
      {icon && <img src={catIconUrl(icon)} alt="" />}
    </span>
  );
}

/* Sığdırma katsayısının sınırları: altında okunmaz, üstünde tabela olur. */
const MIN_K = 0.45;
const MAX_K = 1.8;
const FIT_BASE = 0.0152; // temel punto = sayfa genişliğinin yüzdesi

/** Kutuya sığan EN BÜYÜK katsayı (ikili arama: 7 ölçüm yeter). */
function fitK(el, base) {
  // Taşma iki yönde de aranır: çok sütunlu kutuda fazlalık YANA taşar.
  const fits = () => el.scrollHeight <= el.clientHeight + 1 && el.scrollWidth <= el.clientWidth + 1;
  let lo = MIN_K, hi = MAX_K;
  for (let i = 0; i < 7; i++) {
    const mid = (lo + hi) / 2;
    el.style.fontSize = `${base * mid}px`;
    if (fits()) lo = mid; else hi = mid;
  }
  el.style.fontSize = `${base * lo}px`;
  return lo;
}

/**
 * Sayfadaki bütün yazı kutularını AYNI katsayıyla ölçekler.
 *
 * NEDEN ORTAK: bölmeli şablonda her bölme kendi başına sığdırılırsa bir
 * bölmede iri, yanındakinde minik yazı çıkıyor — tek sayfada iki punto
 * amatör duruyor. En dar bölme neye izin veriyorsa hepsi onu kullanır.
 *
 * NEDEN px: punto `cqw` ile verilince tarayıcı kap sorgusunu bir sonraki
 * düzene erteliyor, aynı karede ölçülen `scrollHeight` eski punto ile
 * geliyordu (büyütme döngüsü taşmayı göremiyordu). Burada piksel yazılıp
 * piksel ölçülüyor.
 */
function useFitPage(pageRef, deps) {
  useLayoutEffect(() => {
    const page = pageRef.current;
    if (!page) return;
    const els = [...page.querySelectorAll('.dz-fit')];
    if (!els.length) return;
    const base = page.clientWidth * FIT_BASE;
    // %1.5 pay: ölçüm ekranda, baskı kâğıtta yapılıyor. Kâğıt dar gelirse
    // (A5, kenar payı) punto sabit kaldığı için taşar — kıl payı sığmasın.
    const k = Math.min(...els.map((el) => fitK(el, base))) * 0.985;
    els.forEach((el) => { el.style.fontSize = `${base * k}px`; });
    // eslint-disable-next-line
  }, deps);
}

/**
 * @param fill  Kutusunu doldursun mu (tasarım şablonu). `size` o durumda
 *              yalnız ÇÖZÜNÜRLÜK: canvas büyük çizilir, kutuya küçültülür.
 */
function Qr({ text, size, label, fill = false }) {
  const ref = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    // Hata düzeltme seviyesi M: lekeli/kırışık kâğıtta okunurluk için yeterli,
    // H'ye çıkarmak kodu gereksiz yoğunlaştırır (URL zaten kısa tutuldu).
    QRCode.toCanvas(ref.current, text, { width: size, margin: 1, errorCorrectionLevel: 'M' })
      .then(() => {
        // TUZAK: qrcode canvas'a SATIR İÇİ ölçü yazıyor (`style.width/height`
        // piksel) ve bu, stil dosyasındaki kuralı eziyor → 420px'lik canvas
        // kutusunu tanımayıp sayfanın dışına taşıyordu. Satır içi ölçüyü
        // burada geri alıyoruz.
        if (fill && ref.current) {
          ref.current.style.width = '100%';
          ref.current.style.height = '100%';
        }
      })
      .catch(() => setFailed(true));
  }, [text, size, fill]);

  if (failed) return <p className="err">{label}</p>;
  return <canvas ref={ref} width={size} height={size} />;
}
